/**
 * PanTiltZoomControl
 * Interactive 3D camera controller providing high-precision pan, tilt, rotation,
 * and zoom controls when 3D terrain mode is active.
 * Incorporates continuous press-and-hold interaction, real-time compass needle orientation,
 * live pitch and zoom readouts, and one-click exit back to 2D top-down view.
 */

import { store } from '../state';

export class PanTiltZoomControl {
  private container: HTMLElement | null = null;
  private map: any = null;
  private isVisible: boolean = false;
  private activeInterval: number | null = null;
  private activeTimeout: number | null = null;

  // DOM elements for live telemetry updates
  private compassNeedle: SVGElement | null = null;
  private pitchBadge: HTMLElement | null = null;
  private zoomBadge: HTMLElement | null = null;

  constructor(private parentContainer: HTMLElement) {
    this.render();
    this.setupStoreSubscription();
  }

  public getIsVisible(): boolean {
    return this.isVisible;
  }

  public setMap(mapInstance: any) {
    this.map = mapInstance;
    if (!this.map) return;

    this.map.on('pitch', () => this.updateTelemetry());
    this.map.on('rotate', () => this.updateTelemetry());
    this.map.on('zoom', () => this.updateTelemetry());
    this.updateTelemetry();
  }

  private render() {
    if (document.getElementById('gw-pan-tilt-zoom-control')) return;

    this.container = document.createElement('div');
    this.container.id = 'gw-pan-tilt-zoom-control';
    this.container.className = 'gw-3d-camera-control gw-hidden';
    this.container.setAttribute('aria-label', '3D Camera Navigation Controls');

    this.container.innerHTML = `
      <div class="gw-3d-control-header">
        <div class="gw-3d-header-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3l4 8 5-5 5 15H2L8 3z"></path>
          </svg>
          <span>3D Camera</span>
        </div>
        <div class="gw-3d-header-actions">
          <button type="button" class="gw-3d-exit-btn" id="gw-3d-exit-btn" title="Exit 3D mode and return to 2D top-down view">
            2D View
          </button>
          <button type="button" class="gw-3d-close-btn" id="gw-3d-close-btn" title="Hide 3D Camera Controls">
            ✕
          </button>
        </div>
      </div>

      <div class="gw-3d-control-body">
        <!-- 1. Directional Pan D-Pad -->
        <div class="gw-3d-dpad-section">
          <div class="gw-3d-section-label">Pan Camera</div>
          <div class="gw-3d-dpad-grid">
            <button type="button" class="gw-3d-dpad-btn gw-dpad-up" data-pan="up" title="Pan North / Up">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h16z"/></svg>
            </button>
            <button type="button" class="gw-3d-dpad-btn gw-dpad-left" data-pan="left" title="Pan West / Left">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M4 12l8-8v16z"/></svg>
            </button>
            <div class="gw-3d-dpad-center" title="Hold any direction button to pan smoothly">
              <div class="gw-3d-dpad-dot"></div>
            </div>
            <button type="button" class="gw-3d-dpad-btn gw-dpad-right" data-pan="right" title="Pan East / Right">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12l-8-8v16z"/></svg>
            </button>
            <button type="button" class="gw-3d-dpad-btn gw-dpad-down" data-pan="down" title="Pan South / Down">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 20l8-8H4z"/></svg>
            </button>
          </div>
        </div>

        <div class="gw-3d-dual-controls">
          <!-- 2. Tilt / Pitch Controls -->
          <div class="gw-3d-sub-section">
            <div class="gw-3d-section-header">
              <span class="gw-3d-section-label">Tilt</span>
              <span class="gw-3d-telemetry-badge" id="gw-3d-pitch-badge">0°</span>
            </div>
            <div class="gw-3d-btn-pair">
              <button type="button" class="gw-3d-action-btn" data-action="tilt-up" title="Tilt Up towards horizon (+5°)">
                ▲ Up
              </button>
              <button type="button" class="gw-3d-action-btn" data-action="tilt-down" title="Tilt Down towards top-down (-5°)">
                ▼ Down
              </button>
            </div>
          </div>

          <!-- 3. Rotation / Bearing Controls -->
          <div class="gw-3d-sub-section">
            <div class="gw-3d-section-header">
              <span class="gw-3d-section-label">Rotate</span>
              <button type="button" class="gw-3d-north-btn" id="gw-3d-north-btn" title="Click to reset North orientation (Bearing 0°)">
                <svg id="gw-3d-compass-needle" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <polygon points="12 2 15 12 12 10 9 12" fill="#ef4444"/>
                  <polygon points="12 22 15 12 12 10 9 12" fill="#94a3b8"/>
                </svg>
                <span>N</span>
              </button>
            </div>
            <div class="gw-3d-btn-pair">
              <button type="button" class="gw-3d-action-btn" data-action="rotate-ccw" title="Rotate Counter-Clockwise (-10°)">
                ↺ Left
              </button>
              <button type="button" class="gw-3d-action-btn" data-action="rotate-cw" title="Rotate Clockwise (+10°)">
                ↻ Right
              </button>
            </div>
          </div>
        </div>

        <!-- 4. Zoom & Reset Row -->
        <div class="gw-3d-bottom-row">
          <div class="gw-3d-zoom-group">
            <span class="gw-3d-section-label">Zoom</span>
            <div class="gw-3d-zoom-btns">
              <button type="button" class="gw-3d-zoom-btn" data-action="zoom-in" title="Zoom In">+</button>
              <button type="button" class="gw-3d-zoom-btn" data-action="zoom-out" title="Zoom Out">−</button>
            </div>
            <span class="gw-3d-telemetry-badge" id="gw-3d-zoom-badge">z--</span>
          </div>

          <button type="button" class="gw-3d-reset-btn" id="gw-3d-reset-btn" title="Reset Camera to Top-Down North View">
            Reset View
          </button>
        </div>
      </div>
    `;

    this.parentContainer.appendChild(this.container);

    this.compassNeedle = this.container.querySelector('#gw-3d-compass-needle');
    this.pitchBadge = this.container.querySelector('#gw-3d-pitch-badge');
    this.zoomBadge = this.container.querySelector('#gw-3d-zoom-badge');

    this.bindEvents();
  }

  private bindEvents() {
    if (!this.container) return;

    // Exit 3D View button
    const exitBtn = this.container.querySelector('#gw-3d-exit-btn');
    if (exitBtn) {
      exitBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.exit3DMode();
      });
    }

    // Close button (hides controls without exiting 3D)
    const closeBtn = this.container.querySelector('#gw-3d-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ panTiltZoomEnabled: false });
      });
    }

    // Reset View button (Bearing 0, Pitch 0)
    const resetBtn = this.container.querySelector('#gw-3d-reset-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.map) {
          this.map.easeTo({
            pitch: 0,
            bearing: 0,
            duration: 600
          });
        }
      });
    }

    // Reset North compass needle button
    const northBtn = this.container.querySelector('#gw-3d-north-btn');
    if (northBtn) {
      northBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.map) {
          this.map.easeTo({
            bearing: 0,
            duration: 500
          });
        }
      });
    }

    // Bind D-pad pan buttons with press-and-hold continuous panning
    this.container.querySelectorAll('.gw-3d-dpad-btn').forEach(btn => {
      const dir = (btn as HTMLElement).dataset.pan;
      if (!dir) return;

      this.attachHoldAction(btn as HTMLElement, () => {
        this.executePan(dir);
      });
    });

    // Bind Tilt & Rotate action buttons with press-and-hold
    this.container.querySelectorAll('.gw-3d-action-btn').forEach(btn => {
      const action = (btn as HTMLElement).dataset.action;
      if (!action) return;

      this.attachHoldAction(btn as HTMLElement, () => {
        this.executeAction(action);
      });
    });

    // Bind Zoom buttons with press-and-hold
    this.container.querySelectorAll('.gw-3d-zoom-btn').forEach(btn => {
      const action = (btn as HTMLElement).dataset.action;
      if (!action) return;

      this.attachHoldAction(btn as HTMLElement, () => {
        this.executeAction(action);
      });
    });
  }

  private attachHoldAction(element: HTMLElement, callback: () => void) {
    const startAction = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.stopAction();

      // Trigger initial step immediately
      callback();

      // Start repeating interval after 260ms delay
      this.activeTimeout = window.setTimeout(() => {
        this.activeInterval = window.setInterval(() => {
          callback();
        }, 80);
      }, 260);
    };

    const stopAction = () => {
      this.stopAction();
    };

    element.addEventListener('mousedown', startAction);
    element.addEventListener('mouseup', stopAction);
    element.addEventListener('mouseleave', stopAction);

    element.addEventListener('touchstart', startAction, { passive: false });
    element.addEventListener('touchend', stopAction);
    element.addEventListener('touchcancel', stopAction);
  }

  private stopAction() {
    if (this.activeTimeout !== null) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null;
    }
    if (this.activeInterval !== null) {
      clearInterval(this.activeInterval);
      this.activeInterval = null;
    }
  }

  private executePan(direction: string) {
    if (!this.map) return;
    const distance = 80; // pixels per step

    let dx = 0;
    let dy = 0;
    switch (direction) {
      case 'up':
        dy = -distance;
        break;
      case 'down':
        dy = distance;
        break;
      case 'left':
        dx = -distance;
        break;
      case 'right':
        dx = distance;
        break;
    }

    this.map.panBy([dx, dy], { duration: 150 });
  }

  private executeAction(action: string) {
    if (!this.map) return;

    switch (action) {
      case 'tilt-up': {
        const curPitch = this.map.getPitch();
        const nextPitch = Math.min(85, curPitch + 5);
        this.map.easeTo({ pitch: nextPitch, duration: 100 });
        break;
      }
      case 'tilt-down': {
        const curPitch = this.map.getPitch();
        const nextPitch = Math.max(0, curPitch - 5);
        this.map.easeTo({ pitch: nextPitch, duration: 100 });
        break;
      }
      case 'rotate-ccw': {
        const curBearing = this.map.getBearing();
        this.map.easeTo({ bearing: curBearing - 10, duration: 100 });
        break;
      }
      case 'rotate-cw': {
        const curBearing = this.map.getBearing();
        this.map.easeTo({ bearing: curBearing + 10, duration: 100 });
        break;
      }
      case 'zoom-in': {
        const curZoom = this.map.getZoom();
        this.map.easeTo({ zoom: Math.min(18, curZoom + 0.35), duration: 120 });
        break;
      }
      case 'zoom-out': {
        const curZoom = this.map.getZoom();
        this.map.easeTo({ zoom: Math.max(1, curZoom - 0.35), duration: 120 });
        break;
      }
    }
  }

  private exit3DMode() {
    if (this.map) {
      this.map.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 500
      });
    }
    store.update({
      terrain3dEnabled: false,
      panTiltZoomEnabled: false,
      savedBasemapOverrideState: null
    });
  }

  public updateTelemetry() {
    if (!this.map || !this.container) return;

    const pitch = Math.round(this.map.getPitch() || 0);
    const bearing = this.map.getBearing() || 0;
    const zoom = (this.map.getZoom() || 0).toFixed(1);

    if (this.pitchBadge) {
      this.pitchBadge.innerText = `${pitch}°`;
    }

    if (this.zoomBadge) {
      this.zoomBadge.innerText = `z${zoom}`;
    }

    if (this.compassNeedle) {
      // Rotate needle opposite to map bearing so needle always points North
      this.compassNeedle.style.transform = `rotate(${-bearing}deg)`;
      this.compassNeedle.style.transformOrigin = 'center center';
    }
  }

  private setupStoreSubscription() {
    let lastPtzState = store.getState().panTiltZoomEnabled;
    this.updateVisibility(lastPtzState);

    store.subscribe((state) => {
      if (state.panTiltZoomEnabled !== lastPtzState) {
        lastPtzState = state.panTiltZoomEnabled;
        this.updateVisibility(lastPtzState);
      }
    });
  }

  private updateVisibility(enabled: boolean) {
    if (!this.container) return;

    if (enabled) {
      this.container.classList.remove('gw-hidden');
      this.isVisible = true;
      this.updateTelemetry();
    } else {
      this.container.classList.add('gw-hidden');
      this.isVisible = false;
      this.stopAction();
    }
  }

  public destroy() {
    this.stopAction();
    if (this.container && this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
    this.container = null;
    this.map = null;
  }
}
